import { categories, videos } from "@/mocks/homeData";
import SectionHeading from "./SectionHeading";

interface ExploreSectionProps {
  query: string;
  onQueryChange: (value: string) => void;
  activeTag: string;
  onTagChange: (tag: string) => void;
}

export default function ExploreSection({ query, onQueryChange, activeTag, onTagChange }: ExploreSectionProps) {
  const keyword = query.trim().toLowerCase();

  const filtered = videos.filter((video) => {
    const matchCategory =
      activeTag === "全部" || video.category === activeTag || video.tags.includes(activeTag);
    if (!matchCategory) return false;
    if (!keyword) return true;
    const haystack = [video.title, video.author, video.category, video.desc, ...video.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });

  const reset = () => {
    onQueryChange("");
    onTagChange("全部");
  };

  return (
    <section id="explore" className="w-full bg-background-100 py-14 md:py-16">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <SectionHeading
          eyebrow="内容探索"
          title="搜索或点选标签，只看你想要的内容"
          desc="下方内容可直接在本页筛选：输入关键词，或点选分类标签，结果会立即更新。筛选仅作用于本页已有内容，不会跳转，也不会上传任何数据。"
        />

        <div className="mt-7 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2">
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              type="text"
              name="explore_search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="搜索标题、作者或标签，如：治愈 / 拆解 / 阿凉"
              className="h-9 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {query ? (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => onQueryChange("")}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-circle-line text-lg"></i>
              </button>
            ) : null}
          </div>

          <div className="no-scrollbar flex items-center gap-2 overflow-x-auto pb-1">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => onTagChange(cat)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeTag === cat
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-foreground-500">
              共找到 <span className="font-heading font-bold text-primary-700">{filtered.length}</span> 条内容
              {activeTag !== "全部" ? `（分类：${activeTag}）` : ""}
            </p>
            {(query || activeTag !== "全部") ? (
              <button
                type="button"
                onClick={reset}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            ) : null}
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((video) => (
              <article
                key={video.id}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative h-52 w-full overflow-hidden sm:h-56">
                  <img
                    src={video.cover}
                    alt={`${video.title} · ${video.category}类小视频`}
                    title={`${video.title} · 推荐小视频`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute bottom-2 right-2 rounded-md bg-foreground-950/70 px-2 py-0.5 font-label text-[10px] font-semibold text-background-50">
                    {video.duration}
                  </span>
                  <span className="absolute left-2 top-2 rounded-md bg-accent-500 px-2 py-0.5 font-label text-[10px] font-semibold text-foreground-950">
                    {video.category}
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-950">{video.title}</h3>
                  <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                    <span className="truncate">{video.author}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {video.views}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-7 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm font-semibold text-foreground-800">没有找到符合条件的内容</p>
            <p className="mt-1 text-xs text-foreground-500">换个关键词，或点选其他标签试试</p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}